/**
 * WebShell — desktop / tablet 3-column layout.
 *
 * Pixel-faithful port of `WebShell` in `screens-web-shell.jsx`:
 *
 *   ┌─────────┬──────────────────────────┬──────────────┐
 *   │ Sidebar │ Topbar                   │              │
 *   │ (244)   ├──────────────────────────┤  RightRail   │
 *   │         │ <children>               │  (320, wide  │
 *   │         │                          │   only)      │
 *   └─────────┴──────────────────────────┴──────────────┘
 *
 * Tablet → Sidebar collapsed (72), no RightRail.
 * Wide   → Sidebar expanded (244), RightRail visible.
 */
import type { ReactNode } from 'react';

import InstallPrompt from '@/components/common/InstallPrompt';
import RightRail from './RightRail';
import Sidebar, { type SidebarTabKey } from './Sidebar';
import Topbar from './Topbar';
import { type Viewport } from '@/lib/viewport';

export interface WebShellProps {
  viewport: Viewport;
  active: SidebarTabKey;
  onTab: (k: SidebarTabKey) => void;
  onAdd?: () => void;
  onSettings?: () => void;
  onSearch?: () => void;
  onNotif?: () => void;
  onBookClick?: () => void;
  bookName?: string;
  bookColor?: string;
  title: string;
  subtitle?: string;
  topbarRight?: ReactNode;
  /** Render right-rail content when on `wide`. Falls back to placeholder. */
  rightRail?: ReactNode;
  children: ReactNode;
  hasUnreadNotif?: boolean;
}

export default function WebShell({
  viewport,
  active,
  onTab,
  onAdd,
  onSettings,
  onSearch,
  onNotif,
  onBookClick,
  bookName,
  bookColor,
  title,
  subtitle,
  topbarRight,
  rightRail,
  children,
  hasUnreadNotif,
}: WebShellProps): JSX.Element {
  const collapsed = viewport === 'tablet';
  const showRail = viewport === 'wide';

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        background: 'var(--bg-app)',
      }}
    >
      <Sidebar
        active={active}
        onTab={onTab}
        onAdd={onAdd}
        onSettings={onSettings}
        onBookClick={onBookClick}
        bookName={bookName}
        bookColor={bookColor}
        collapsed={collapsed}
      />
      <main
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Topbar
          title={title}
          subtitle={subtitle}
          onSearch={onSearch}
          onNotif={onNotif}
          right={topbarRight}
          hasUnreadNotif={hasUnreadNotif}
        />
        <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
      </main>
      {showRail && <RightRail>{rightRail}</RightRail>}
      <InstallPrompt />
    </div>
  );
}
